import axios from 'axios'
import type { CourseSessionDto, CoursePricingModel } from './admin'

const BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

// Unauthenticated axios instance for public endpoints
const publicApi = axios.create({ baseURL: BASE_URL })

export type CertificateVerificationDto = {
  recipientName: string
  courseName: string
  completedDate: string   // "YYYY-MM-DD"
  expiryDate: string | null
  status: 'Valid' | 'DueSoon' | 'Overdue' | 'NoExpiry'
  companyName: string | null
  companyLogoUrl: string | null
  companyPrimaryColour: string | null
  region: string
  timeZoneId: string
  /** Only true when the tenant shows photos on this page and this certificate is current. */
  hasPhoto: boolean
  photoSource: 'Admin' | 'ClientManager' | 'Self' | 'Trainer' | null
  identityCheckedOn: string | null   // "YYYY-MM-DD"
  /** True when the training provider's administrator recorded the check, rather than the trainer at the course. */
  identityCheckedByProvider: boolean
}

/** A website picture at (about) the given width: the stored size at least that wide, else the largest. */
export const publicWebsiteImageUrl = (id: string, width?: number) =>
  `${BASE_URL}/public/website-images/${id}${width ? `?w=${width}` : ''}`

export type PublicCourseImage = {
  id: string
  altText: string
  focusX: number
  focusY: number
  width: number
  height: number
}

export type PublicCategory = {
  slug: string
  name: string
  description: string | null
  image: PublicCourseImage | null
}

export type PublicCourseSummary = {
  slug: string
  name: string
  categorySlug: string | null
  summary: string | null
  duration: string | null
  image: PublicCourseImage | null
  price: number | null
  pricingModel: CoursePricingModel
  flatFeeAmount: number | null
  flatFeeCapacity: number | null
  nextSessionStartsAt: string | null
  nextSessionPlacesLeft: number | null
  upcomingSessions: number
  websiteUrl: string | null
}

type PublicCourseBranding = {
  tenantName: string
  tenantLogoUrl: string | null
  primaryColour: string | null
  secondaryColour: string | null
  currency: string
  region: string
  timeZoneId: string
  contactEmail: string | null
  website: string | null
}

export type PublicCourseCatalogue = PublicCourseBranding & {
  categories: PublicCategory[]
  courses: PublicCourseSummary[]
}

export type PublicCoursePage = PublicCourseBranding & {
  slug: string
  name: string
  code: string | null
  category: PublicCategory | null
  summary: string | null
  /** Safe HTML made on the server from the customer's markdown. */
  overviewHtml: string
  duration: string | null
  whoItsFor: string | null
  whatYoullLearn: string[]
  requirements: string[]
  whatsIncluded: string[]
  images: PublicCourseImage[]
  price: number | null
  pricingModel: CoursePricingModel
  flatFeeAmount: number | null
  flatFeeCapacity: number | null
  isThirdPartyCertified: boolean
  thirdPartyProviderName: string | null
  websiteUrl: string | null
  noIndex: boolean
  sessions: CourseSessionDto[]
}

export type PublicSessionsResponse = {
  tenantName: string
  tenantLogoUrl: string | null
  primaryColour: string | null
  secondaryColour: string | null
  termsVersion: number
  sessions: CourseSessionDto[]
  currency: string
  region: string
  timeZoneId: string
}

export type PublicSessionDetailResponse = {
  tenantName: string
  tenantLogoUrl: string | null
  primaryColour: string | null
  secondaryColour: string | null
  termsVersion: number
  session: CourseSessionDto
  currency: string
  region: string
  timeZoneId: string
  /** The training company uses identity photos, so the booking page tells people one may be taken. */
  photosEnabled: boolean
}

export type PublicBookingAttendee = {
  fullName: string
  email: string
  companyName: string | null
  marketingConsent: boolean
}

export type PublicBookingRequest = {
  contactName: string
  contactEmail: string
  contactPhone: string
  attendees: PublicBookingAttendee[]
  notes: string | null
  termsAccepted: boolean
}

export type PublicBookingAttendeeDetail = {
  fullName: string
  companyName: string | null
}

export type PublicBookingDetailResponse = {
  bookingId: string
  courseName: string
  courseWebsiteUrl: string | null
  startsAt: string
  endsAt: string | null
  venueName: string | null
  venueCity: string | null
  venuePostcode: string | null
  deliveryMethod: 'InPerson' | 'Online'
  meetingUrl: string | null
  meetingInstructions: string | null
  contactName: string
  contactEmail: string
  contactPhone: string | null
  placesRequested: number
  totalAmount: number | null
  pricingModel: CoursePricingModel
  flatFeeCapacity: number | null
  status: string
  notes: string | null
  attendees: PublicBookingAttendeeDetail[]
  isPaid: boolean
  paymentLink: string | null
  stripeConnectEnabled: boolean
  currency: string
  region: string
  timeZoneId: string
}

/** Direct PDF download URL for a certificate (the GUID is the access capability). */
export const certificateDownloadUrl = (certificateId: string) =>
  `${BASE_URL}/public/verify/${certificateId}/download`

/** The holder's photo for the verification page (404 unless the tenant shows photos there). */
export const certificatePhotoUrl = (certificateId: string) =>
  `${BASE_URL}/public/verify/${certificateId}/photo`

/** Same file, rendered inline (no Content-Disposition: attachment) for a "View" action. */
export const certificateViewUrl = (certificateId: string) =>
  `${BASE_URL}/public/verify/${certificateId}/download?inline=true`

/** Direct PDF URL for a tenant's Terms & Conditions — public, no auth required. */
export const publicTermsUrl = (slug: string) =>
  `${BASE_URL}/public/sessions/${slug}/terms`

/** A training company's photo privacy notice (null from the API when the organisation doesn't exist). */
export type PublicPhotoNotice = {
  tenantName: string
  logoUrl: string | null
  primaryColour: string | null
  enabled: boolean
  notice: string | null
  showOnVerification: boolean
  contactEmail: string | null
}

/** An in-app legal document edited by the SuperAdmin. */
export type PublicLegalDocument = {
  key: string
  title: string
  subtitle: string | null
  body: string
  version: number
  effectiveOn: string   // "YYYY-MM-DD"
}

export const publicApiClient = {
  getPhotoNotice: (slug: string) =>
    publicApi.get<PublicPhotoNotice>(`/public/photo-notice/${slug}`).then(r => r.data)
      .catch(err => (err?.response?.status === 404 ? null : Promise.reject(err))),

  /** The edited text, or null when it hasn't been edited (the app then shows its built-in text). */
  getLegalDocument: (key: string) =>
    publicApi.get<PublicLegalDocument>(`/legal/documents/${key}`).then(r => r.data)
      .catch(err => (err?.response?.status === 404 ? null : Promise.reject(err))),

  verifyCertificate: (certificateId: string) =>
    publicApi.get<CertificateVerificationDto>(`/public/verify/${certificateId}`),

  getPublicCourseCatalogue: (tenantSlug: string) =>
    publicApi.get<PublicCourseCatalogue>(`/public/courses/${tenantSlug}`),

  getPublicCoursePage: (tenantSlug: string, courseSlug: string) =>
    publicApi.get<PublicCoursePage>(`/public/courses/${tenantSlug}/${courseSlug}`),

  getPublicSessions: (slug: string) =>
    publicApi.get<PublicSessionsResponse>(`/public/sessions/${slug}`),

  getPublicSession: (slug: string, sessionId: string) =>
    publicApi.get<PublicSessionDetailResponse>(`/public/sessions/${slug}/${sessionId}`),

  createPublicBooking: (slug: string, sessionId: string, data: PublicBookingRequest) =>
    publicApi.post<{ bookingId: string; checkoutUrl: string | null }>(`/public/sessions/${slug}/${sessionId}/book`, data),

  getPublicBooking: (slug: string, bookingId: string) =>
    publicApi.get<PublicBookingDetailResponse>(`/public/sessions/${slug}/bookings/${bookingId}`),

  createSessionBookingCheckout: (slug: string, bookingId: string) =>
    publicApi.post<{ url: string }>(`/public/sessions/${slug}/bookings/${bookingId}/checkout`, {}),
}
